import Chip from '@mui/material/Chip';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';

import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  allItems,
  buildFileNamesText,
  commentsByItem,
  unlockGallerySelectionRequest,
  type Gallery,
  type GalleryComment,
  type GalleryItem,
} from 'gallery';

import type { GalleryAction } from './gallery-page';
import {
  copyToClipboard,
  formatDateTime,
  notifyGalleryError,
  notifyGallerySuccess,
  useGalleryText,
} from './gallery-ui';

interface GallerySelectionPanelProps {
  gallery: Gallery;
  token?: string;
  run: GalleryAction;
}

// The selected files with their comments, then any other commented file, so no guest comment is
// out of the Provider's sight.
function rowsOf(gallery: Gallery): Array<{ item: GalleryItem; picked: boolean }> {
  const commented = new Set(gallery.comments.map((comment) => comment.itemUuid));
  const picked = new Set(gallery.selection.itemUuids);
  const others = allItems(gallery).filter(
    (item) => commented.has(item.uuid) && !picked.has(item.uuid)
  );
  return [
    ...gallery.selection.items.map((item) => ({ item, picked: true })),
    ...others.map((item) => ({ item, picked: false })),
  ];
}

export function GallerySelectionPanel({ gallery, token, run }: GallerySelectionPanelProps) {
  const { locale, t } = useGalleryText();
  const { selection } = gallery;
  const comments = commentsByItem(gallery.comments) as Map<string, GalleryComment[]>;
  const rows = rowsOf(gallery);
  const heading = gallery.selectionLimit
    ? interpolateTemplate(t.selection.headingWithLimit, {
        count: String(selection.count),
        limit: String(gallery.selectionLimit),
      })
    : interpolateTemplate(t.selection.heading, { count: String(selection.count) });

  const copyNames = async () => {
    if (await copyToClipboard(buildFileNamesText(selection.fileNames))) {
      notifyGallerySuccess(t.title, t.selection.namesCopied);
    } else {
      notifyGalleryError(t.title, t.share.copyFailed);
    }
  };

  const unlock = () =>
    run(() => unlockGallerySelectionRequest(gallery.uuid, token), {
      success: t.selection.unlocked,
    });

  return (
    <SoftBox display="flex" flexDirection="column" gap={2} data-testid="gallery-selection">
      <SoftBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <SoftTypography variant="h6">{heading}</SoftTypography>
        {selection.locked && selection.sentAt ? (
          <Chip
            color="warning"
            label={interpolateTemplate(t.selection.sentAt, {
              date: formatDateTime(selection.sentAt, locale),
            })}
            data-testid="gallery-selection-sent"
          />
        ) : (
          <Chip label={t.selection.notSent} data-testid="gallery-selection-open" />
        )}
      </SoftBox>
      <SoftBox display="flex" gap={1} flexWrap="wrap">
        <SoftButton
          variant="gradient"
          color="info"
          disabled={!selection.count}
          onClick={copyNames}
          data-testid="gallery-copy-file-names"
        >
          {t.selection.copyNames}
        </SoftButton>
        {selection.locked ? (
          <SoftButton
            variant="outlined"
            color="info"
            onClick={unlock}
            data-testid="gallery-unlock-selection"
          >
            {t.selection.unlock}
          </SoftButton>
        ) : null}
      </SoftBox>
      {rows.length ? (
        <SoftBox display="flex" flexDirection="column" gap={1}>
          {rows.map(({ item, picked }) => (
            <SoftBox
              key={item.uuid}
              display="flex"
              gap={2}
              alignItems="flex-start"
              data-testid="gallery-selection-item"
            >
              {item.thumb400Url ? (
                <img
                  src={item.thumb400Url}
                  alt=""
                  style={{
                    width: 72,
                    height: 72,
                    objectFit: 'cover',
                    borderRadius: 6,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <SoftBox
                  width={72}
                  height={72}
                  borderRadius="6px"
                  bgcolor="grey.200"
                  flexShrink={0}
                />
              )}
              <SoftBox minWidth={0}>
                <SoftTypography variant="button" fontWeight="bold">
                  {picked ? `♥ ${item.media.fileName}` : item.media.fileName}
                </SoftTypography>
                {(comments.get(item.uuid) ?? []).map((comment) => (
                  <SoftTypography
                    key={comment.uuid}
                    variant="body2"
                    data-testid="gallery-selection-comment"
                  >
                    {`💬 ${comment.text} `}
                    <SoftTypography component="span" variant="caption" color="text">
                      {`— ${comment.author?.trim() || t.selection.anonymous}`}
                    </SoftTypography>
                  </SoftTypography>
                ))}
              </SoftBox>
            </SoftBox>
          ))}
        </SoftBox>
      ) : (
        <SoftTypography variant="button" fontWeight="regular" color="text">
          {t.selection.empty}
        </SoftTypography>
      )}
    </SoftBox>
  );
}
